import React from 'react';
import { Descriptions, Alert, Statistic, Space, Typography } from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { PlanChangePreview } from '@/types/subscription';
import { withDevModalFilePath } from '../common';

const { Modal } = withDevModalFilePath('client/src/components/subscription/PlanChangePreviewDialog.tsx');

export interface PlanChangePreviewDialogProps {
  open: boolean;
  preview: PlanChangePreview | null;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLoading?: boolean;
  descriptionMap?: Partial<Record<string, string>>;
}

const currencyFormatter = new Intl.NumberFormat('ja-JP', {
  style: 'currency',
  currency: 'JPY',
  maximumFractionDigits: 0,
});

const formatCurrency = (value: number | undefined) =>
  typeof value === 'number' ? currencyFormatter.format(value) : '¥0';

const formatDate = (iso: string | undefined) =>
  iso ? dayjs(iso).format('YYYY年MM月DD日') : '未定';

export const PlanChangePreviewDialog: React.FC<PlanChangePreviewDialogProps> = ({
  open,
  preview,
  onCancel,
  onConfirm,
  confirmLoading,
  descriptionMap,
}) => {
  const isUpgrade = preview?.changeType === 'upgrade';
  const isDowngrade = preview?.changeType === 'downgrade';
  const planLabel = (code: string) => descriptionMap?.[code] ?? code;

  const renderProration = () => {
    if (!preview || !isUpgrade || !preview.prorationDetails) {
      return null;
    }
    const { chargeAmount, remainingDays, totalDaysInCycle } = preview.prorationDetails;

    return (
      <Alert
        type="info"
        showIcon
        message="即時に差額が請求されます"
        description={
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Statistic
              title="今回の追加請求額"
              value={chargeAmount}
              formatter={(val) => formatCurrency(Number(val))}
            />
            <Typography.Paragraph style={{ marginBottom: 0, fontSize: 12 }}>
              {`残り${remainingDays}日 / ${totalDaysInCycle}日間の差額分が請求対象です。`}
            </Typography.Paragraph>
            <Typography.Paragraph style={{ marginBottom: 0, fontSize: 12 }}>
              {`次回請求以降は ${formatCurrency(preview.newPlan.amount)} / 月 が適用されます。`}
            </Typography.Paragraph>
          </Space>
        }
      />
    );
  };

  const renderDowngradeNotice = () => {
    if (!preview || !isDowngrade) {
      return null;
    }

    return (
      <Alert
        type="warning"
        showIcon
        message="次回請求日から新しいプランが適用されます"
        description={
          <Typography.Paragraph style={{ marginBottom: 0, fontSize: 12 }}>
            {`${formatDate(preview.effectiveDate)} までは現在のプラン (${formatCurrency(
              preview.currentPlan.amount,
            )}/月) を継続利用できます。日割りでの返金はありません。`}
          </Typography.Paragraph>
        }
      />
    );
  };

  const renderContent = () => {
    if (!preview) {
      return (
        <Alert
          type="warning"
          showIcon
          message="プラン変更プレビューを取得できませんでした"
          description="時間をおいて再度お試しください。"
        />
      );
    }

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Descriptions column={1} bordered>
          <Descriptions.Item label="現在のプラン">
            <Typography.Text strong>{planLabel(preview.currentPlan.code)}</Typography.Text>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              {formatCurrency(preview.currentPlan.amount)} / 月
            </Typography.Paragraph>
          </Descriptions.Item>
          <Descriptions.Item label="変更後のプラン">
            <Typography.Text strong>{planLabel(preview.newPlan.code)}</Typography.Text>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              {formatCurrency(preview.newPlan.amount)} / 月
            </Typography.Paragraph>
          </Descriptions.Item>
          <Descriptions.Item label={isUpgrade ? '適用日時' : '適用日'}>
            {isUpgrade ? '変更確定後すぐに適用されます' : formatDate(preview.effectiveDate)}
          </Descriptions.Item>
        </Descriptions>
        {renderProration()}
        {renderDowngradeNotice()}
      </Space>
    );
  };

  const renderTitle = () => {
    if (!preview) {
      return 'プラン変更プレビュー';
    }
    if (isUpgrade) {
      return (
        <span>
          <UpOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
          プランアップグレード確認
        </span>
      );
    }
    if (isDowngrade) {
      return (
        <span>
          <DownOutlined style={{ marginRight: 8, color: '#1677ff' }} />
          プランダウングレード確認
        </span>
      );
    }
    return 'プラン変更確認';
  };

  return (
    <Modal
      title={renderTitle()}
      open={open}
      onOk={onConfirm}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      okText="変更する"
      cancelText="キャンセル"
      centered
      destroyOnHidden
      maskClosable={false}
      okButtonProps={{ disabled: !preview }}
      data-testid="plan-change-preview-dialog"
    >
      {renderContent()}
    </Modal>
  );
};

export default PlanChangePreviewDialog;
